import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import classes from "../SentimentBiggestChangePercentWidget/SentimentChangePercent.module.css";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useGetSentimentsBreakdownMutation } from "../../../store/api/lumi/sentiment";
import { safeDivide } from "../../../utils/safeDivide";
import { debounce } from "lodash";
import Scrollbars from "react-custom-scrollbars-2";
import { getComparingDates } from "../../../utils/getCompareDates";
import { ArrowUp } from "../../../icons/ArrowUp";
import { ArrowDown } from "../../../icons/ArrowDown";
import LargestSentimentMovementBar from "../SentimentBiggestChangePercentWidget/LargestSentimentMovementBar";
import { Tooltip } from "antd";
import NetSentimentChangeSummary from "./NetSentimentChangeSummary";
import { useSelector } from "react-redux";

const NetSentimentChangeWidget = (props) => {
    const {
        selectedDateName,
        interval,
        id,
        filters,
        setFilterDocCount,
        includeExamples,
        selectedPageSize,
        seletctedType,
        conceptList,
    } = props;
    const [loading, setLoading] = useState(false);
    const [sentimentChanges, setSentimentChanges] = useState([]);
    const [comparingDates, setComparingDates] = useState([]);

    const [getSentimentsBreakdown] = useGetSentimentsBreakdownMutation();

    const [selectedConcept, setSelectedConcept] = useState(null);
    const [filterBaseline, setFilterBaseline] = useState(null);
    const [filterComparison, setFilterComparison] = useState(null);
    const sentiment_share_ai = useSelector(state => state.auth.flags.sentiment_share_ai);

    const conceptFilter = useMemo(
        () =>
            JSON.stringify(
                getConceptFilter({ ...props, selectedPageSize }),
            ),
        [props],
    );

    const getSentimentData = useCallback(async () => {
        setLoading(true);

        const { data } = await getSentimentsBreakdown({
            id,
            concept_selector: JSON.parse(conceptFilter),
            filter: filters,
            include_examples: includeExamples,
            breakdowns: [{ name: selectedDateName, interval }],
        });
        setFilterDocCount(data.filter_count);
        const breakdownData = data.breakdowns[0].buckets;
        // set filters for summary
        setFilterBaseline([
            {
                name: selectedDateName,
                minimum: breakdownData[breakdownData.length - 2].label,
                maximum: breakdownData[breakdownData.length - 1].label,
            },
        ]);
        setFilterComparison([
            {
                name: selectedDateName,
                minimum: breakdownData[breakdownData.length - 1].label,
            },
        ]);

        const lastData = breakdownData[breakdownData.length - 1];
        const preLastData = breakdownData[breakdownData.length - 2];
        let calculatedComparingDates;
        if (preLastData === undefined) {
            calculatedComparingDates = getComparingDates(lastData, lastData, interval);
        } else {
            calculatedComparingDates = getComparingDates(preLastData, lastData, interval);
        }

        setComparingDates(calculatedComparingDates);

        const updatedSentiments = lastData.match_counts.map((sentiment) => {
            const preLastSentiment = preLastData?.match_counts.find(
                (c) => c.name === sentiment.name,
            );

            let changePercentage;
            if (preLastData === undefined) {
                changePercentage = (sentiment.sentiment_share.positive - sentiment.sentiment_share.negative).toFixed(2) * 100;
            } else if (preLastSentiment) {
                changePercentage = (sentiment.sentiment_share.positive - sentiment.sentiment_share.negative
                    - (preLastSentiment.sentiment_share.positive - preLastSentiment.sentiment_share.negative)).toFixed(2) * 100;

            } else {
                changePercentage = safeDivide(
                    sentiment.sentiment_share.positive - sentiment.sentiment_share.negative,
                ) * 100;
            }

            return {
                ...sentiment,
                preLastSentiment: preLastSentiment?.sentiment_share ? preLastSentiment.sentiment_share : {
                    negative: 0,
                    neutral: 0,
                    positive: 0,
                },
                changePercentage,
            };
        });
        const changedConceptsPositive = [...updatedSentiments].sort(
            (a, b) =>
                Math.abs(Number(b.changePercentage)) -
                Math.abs(Number(a.changePercentage)),
        );

        setSentimentChanges(changedConceptsPositive);
        setSelectedConcept(changedConceptsPositive[0]);

        setLoading(false);
    }, [
        getSentimentsBreakdown,
        id,
        filters,
        interval,
        selectedDateName,
        conceptFilter,
        setFilterDocCount,
        selectedPageSize,
        seletctedType,
        conceptList,
    ]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetSentiments = debounce(async () => {
            await getSentimentData();
        }, 500);

        debouncedGetSentiments();

        return () => {
            debouncedGetSentiments.cancel();
        };
    }, [getSentimentData]);

    const height = props.widget.layout.h * 39 - 150;
    let EqualCompareDate = comparingDates[0] === comparingDates[1];

    if (loading) {
        return <Spin
            className={"spinner-by-center"}
            indicator={
                <LoadingOutlined style={{ fontSize: 24 }} spin />
            }
        />;
    }
    return (
        <div className={classes.sentimentContainer}>
            <div className="sentiment-container">
                <div className="sentiment positive">
                    <div className="color-box"></div>
                    Positive
                </div>
                <div className="sentiment neutral">
                    <div className="color-box"></div>
                    Neutral
                </div>
                <div className="sentiment negative">
                    <div className="color-box"></div>
                    Negative
                </div>
            </div>

            <div className={classes.change_types_container}>
                <div className={classes.change_type_container}>
                    <div className={classes.change_type_items}>
                        <div className={classes.change_type}>
                            <div className={classes.gridHeader}>
                                <div className={classes.headerGridContainer}>
                                    <div className={classes.gridItemHeader}>Concept</div>
                                    <div className={classes.gridItemHeader}>Change</div>
                                    <div className={classes.gridItemHeader}>Date</div>
                                    <div className={classes.gridItemHeader}>Sentiment</div>
                                </div>
                            </div>
                            <Scrollbars style={{ height: height }}>
                                <div className={classes.gridContainer}>
                                    {sentimentChanges.map((concept, index) => (
                                        <div key={concept.name + index} className={classes.gridRow}>
                                            <Tooltip title={concept.name}>
                                                <div style={{ fontSize: "1.2rem", cursor: "pointer" }}
                                                     className={classes.gridItemConcept}
                                                >
                                                       <span onMouseDown={(e) => e.stopPropagation()}
                                                             onClick={() => {
                                                                 setSelectedConcept(concept);
                                                             }}> {concept.name}
                                        </span>
                                                </div>
                                            </Tooltip>

                                            <div className={classes.gridItemChanges}>
                                                {
                                                    !EqualCompareDate && (concept.changePercentage >= 0 ? <ArrowUp /> :
                                                        <ArrowDown />)
                                                }
                                                <span
                                                    style={{ fontSize: "1.2rem" }}>
                                                         {EqualCompareDate ? "0" : Math.abs(concept.changePercentage).toFixed(1)}%
                                                    </span>
                                            </div>
                                            <div className={classes.gridItemMovement}>
                                                <div className={classes.sentimentLastDate}>
                                                    {comparingDates[0]}
                                                </div>
                                                {
                                                    !EqualCompareDate && <div className={classes.sentimentPreLastDate}>
                                                        {comparingDates[1]}
                                                    </div>
                                                }

                                            </div>
                                            <div className={classes.gridItemMovement}>
                                                <div style={{ display: "flex", flexDirection: "column" }}>
                                                    <LargestSentimentMovementBar concept={concept.sentiment_share} />
                                                    {
                                                        !EqualCompareDate &&
                                                        <LargestSentimentMovementBar concept={concept.preLastSentiment}
                                                                                     last={true} />
                                                    }

                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {sentiment_share_ai &&
                                    <NetSentimentChangeSummary
                                        id={id}
                                        selectedConcept={selectedConcept}
                                        filterBaseline={filterBaseline}
                                        filterComparison={filterComparison}
                                    />
                                }
                            </Scrollbars>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default NetSentimentChangeWidget;
